import React, { useState, useCallback } from "react";
import { authFetch } from "@/lib/authFetch";
import { toast } from "sonner";
import {
  Upload, FileText, Download, RefreshCw, Trash2, Search, Filter, Clock, CheckCircle, AlertCircle, Eye, ChevronDown, X, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "ID", "Proof of Address", "Paystub / Income", "Bank Statement",
  "Credit Report Support", "Dispute Support", "Bankruptcy / Collection Support", "Miscellaneous",
];

const DIRECTION_TABS = [
  { value: "all", label: "All" },
  { value: "internal", label: "Internal" },
  { value: "sent", label: "Sent" },
  { value: "received", label: "Received" },
];

const SyncBadge = ({ status }) => {
  const map = {
    synced: { icon: CheckCircle, cls: "bg-emerald-100 text-emerald-700 border-emerald-300", label: "Synced" },
    pending: { icon: Clock, cls: "bg-amber-100 text-amber-700 border-amber-300", label: "Pending" },
    failed: { icon: AlertCircle, cls: "bg-red-100 text-red-700 border-red-300", label: "Failed" },
    local_only: { icon: FileText, cls: "bg-blue-100 text-blue-700 border-blue-300", label: "Local" },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`text-xs gap-1 ${s.cls}`} data-testid={`sync-badge-${status}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </Badge>
  );
};

const formatSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatDate = (d) => {
  if (!d) return "-";
  try { return new Date(d).toLocaleString(); } catch { return "-"; }
};

const DocumentsTab = ({ clientId, isAdmin, isDarkMode }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [showEvents, setShowEvents] = useState(null);
  const [events, setEvents] = useState([]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab !== "all") params.append("direction", activeTab);
      if (categoryFilter) params.append("category", categoryFilter);
      if (searchQuery) params.append("search", searchQuery);
      const resp = await authFetch(`${API}/api/clients/${clientId}/documents/list?${params}`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error("Error fetching docs:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId, activeTab, categoryFilter, searchQuery]);

  React.useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  React.useEffect(() => {
    if (!isAdmin) return;
    authFetch(`${API}/api/clients/${clientId}/documents-sync-summary`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setSyncSummary(d));
  }, [clientId, isAdmin, documents]);

  const handleDownload = async (doc) => {
    try {
      const resp = await authFetch(`${API}/api/clients/${clientId}/documents/${doc.document_id}/download`, { credentials: "include" });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = doc.file_name; a.click();
        URL.revokeObjectURL(url);
      }
    } catch { toast.error("Download failed"); }
  };

  const handleResync = async (doc) => {
    try {
      const resp = await authFetch(`${API}/api/clients/${clientId}/documents/${doc.document_id}/resync`, { method: "POST", credentials: "include" });
      const data = await resp.json();
      toast[data.success ? "success" : "error"](data.message);
      fetchDocuments();
    } catch { toast.error("Resync failed"); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"?`)) return;
    try {
      const resp = await authFetch(`${API}/api/clients/${clientId}/documents/${doc.document_id}`, { method: "DELETE", credentials: "include" });
      if (resp.ok) { toast.success("Document deleted"); fetchDocuments(); }
    } catch { toast.error("Delete failed"); }
  };

  const handleViewEvents = async (doc) => {
    try {
      const resp = await authFetch(`${API}/api/clients/${clientId}/documents/${doc.document_id}/events`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        setEvents(data.events || []);
        setShowEvents(doc);
      }
    } catch { toast.error("Could not load events"); }
  };

  const bg = isDarkMode ? "bg-[#265324] border-[#1a3d18]" : "";
  const bgInner = isDarkMode ? "bg-[#1a3d18]" : "bg-slate-50";
  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";

  return (
    <div className="space-y-4" data-testid="documents-tab">
      {/* Sync Summary (admin only) */}
      {isAdmin && syncSummary && (
        <div className={`flex flex-wrap gap-3 p-3 rounded-lg text-sm ${bgInner}`} data-testid="sync-summary">
          <span className={textMuted}>Docs: <strong className={isDarkMode ? "text-white" : "text-slate-900"}>{syncSummary.total}</strong></span>
          <span className="text-emerald-600">Synced: {syncSummary.synced}</span>
          <span className="text-blue-600">Local: {syncSummary.local_only}</span>
          {syncSummary.pending > 0 && <span className="text-amber-600">Pending: {syncSummary.pending}</span>}
          {syncSummary.failed > 0 && <span className="text-red-600">Failed: {syncSummary.failed}</span>}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex gap-1 p-1 rounded-lg bg-black/5" data-testid="direction-tabs">
          {DIRECTION_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setActiveTab(t.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === t.value
                  ? (isDarkMode ? "bg-[#22c55e] text-white" : "bg-white text-slate-900 shadow-sm")
                  : (isDarkMode ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900")
              }`}
              data-testid={`tab-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search files..."
              className="pl-8 w-48"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-docs"
            />
          </div>
          <Select value={categoryFilter || "all_cats"} onValueChange={(v) => setCategoryFilter(v === "all_cats" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="category-filter">
              <Filter className="h-3.5 w-3.5 mr-1 text-slate-400" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_cats">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowUpload(true)} className="gap-1.5" data-testid="upload-btn">
            <Upload className="h-4 w-4" /> Upload
          </Button>
        </div>
      </div>

      {/* Document Table */}
      <Card className={bg}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="empty-state">
              <FileText className={`h-12 w-12 ${isDarkMode ? "text-slate-600" : "text-slate-300"}`} />
              <p className={`font-medium ${isDarkMode ? "text-white" : "text-slate-900"}`}>No documents yet</p>
              <p className={`text-sm text-center max-w-sm ${textMuted}`}>
                Upload a file using the button above and it will sync to your Credit Firm account so your counselor can review it right away.
              </p>
              <p className={`text-xs ${textMuted}`}>Supported: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, CSV, TXT (up to 50MB)</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDarkMode ? "border-[#1a3d18]" : ""}>
                    <TableHead>File</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Direction</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.document_id} className={isDarkMode ? "border-[#1a3d18] hover:bg-[#1a3d18]" : "hover:bg-slate-50"} data-testid={`doc-row-${doc.document_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? "text-[#c3ca6d]" : "text-teal-700"}`} />
                          <span className={`font-medium truncate max-w-[200px] ${isDarkMode ? "text-white" : ""}`} title={doc.file_name}>{doc.file_name}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{doc.category || "—"}</Badge></TableCell>
                      <TableCell className={`text-xs ${textMuted}`}>{doc.source_system === "portal" ? "Portal" : "HighLevel"}</TableCell>
                      <TableCell className={`capitalize text-xs ${textMuted}`}>{doc.direction || "—"}</TableCell>
                      <TableCell className={`text-xs ${textMuted}`}>{doc.uploaded_by || "—"}</TableCell>
                      <TableCell className={`text-xs ${textMuted}`}>{formatDate(doc.uploaded_at)}</TableCell>
                      <TableCell className={`text-xs ${textMuted}`}>{formatSize(doc.file_size)}</TableCell>
                      <TableCell><SyncBadge status={doc.sync_status} /></TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(doc)} title="Download" data-testid={`download-${doc.document_id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {isAdmin && doc.sync_status !== "synced" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-500" onClick={() => handleResync(doc)} title="Resync" data-testid={`resync-${doc.document_id}`}>
                              <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewEvents(doc)} title="Audit Log" data-testid={`events-${doc.document_id}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(doc)} title="Delete" data-testid={`delete-${doc.document_id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <UploadModal
        open={showUpload}
        onClose={() => setShowUpload(false)}
        clientId={clientId}
        isDarkMode={isDarkMode}
        onUploaded={() => { setShowUpload(false); fetchDocuments(); }}
      />

      {/* Events Modal */}
      {showEvents && (
        <Dialog open onOpenChange={() => setShowEvents(null)}>
          <DialogContent className={isDarkMode ? "bg-[#265324] border-[#1a3d18] text-white" : ""}>
            <DialogHeader>
              <DialogTitle>Audit Log — {showEvents.file_name}</DialogTitle>
              <DialogDescription className={textMuted}>Document activity history</DialogDescription>
            </DialogHeader>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {events.length === 0 ? (
                <p className={textMuted}>No events recorded.</p>
              ) : events.map((e, i) => (
                <div key={i} className={`p-2 rounded text-sm ${bgInner}`}>
                  <div className="flex justify-between">
                    <span className="font-medium">{e.event_type}</span>
                    <span className={`text-xs ${textMuted}`}>{formatDate(e.created_at)}</span>
                  </div>
                  <p className={`text-xs ${textMuted}`}>{e.actor} ({e.actor_role})</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const UploadModal = ({ open, onClose, clientId, isDarkMode, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("Miscellaneous");
  const [notes, setNotes] = useState("");
  const [direction, setDirection] = useState("received");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setProgress(10);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);
      formData.append("notes", notes);
      formData.append("direction", direction);
      formData.append("visibility", "all");

      setProgress(30);
      const resp = await authFetch(`${API}/api/clients/${clientId}/documents/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      setProgress(90);
      const data = await resp.json();
      if (resp.ok) {
        toast.success(`"${file.name}" uploaded successfully`);
        setFile(null); setNotes(""); setCategory("Miscellaneous");
        onUploaded();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (err) {
      toast.error("Upload failed: " + err.message);
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg ${isDarkMode ? "bg-[#265324] border-[#1a3d18] text-white" : ""}`}>
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription className={textMuted}>
            Upload a file and it will sync to your Credit Firm CRM account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById("doc-file-input").click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-emerald-500 bg-emerald-50/10"
                : (isDarkMode ? "border-[#1a3d18] hover:border-[#22c55e]/50" : "border-slate-200 hover:border-slate-400")
            }`}
            data-testid="drop-zone"
          >
            <input
              id="doc-file-input"
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-emerald-500" />
                <div className="text-left">
                  <p className={`font-medium ${isDarkMode ? "text-white" : ""}`}>{file.name}</p>
                  <p className={`text-sm ${textMuted}`}>{formatSize(file.size)}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className={`h-10 w-10 mx-auto mb-2 ${isDarkMode ? "text-slate-500" : "text-slate-300"}`} />
                <p className={`font-medium ${isDarkMode ? "text-white" : ""}`}>Drag & drop a file here</p>
                <p className={`text-sm ${textMuted}`}>or click to browse</p>
                <p className={`text-xs mt-2 ${textMuted}`}>PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, CSV, TXT — up to 50MB</p>
              </>
            )}
          </div>

          {/* Category */}
          <div>
            <label className={`text-sm font-medium mb-1 block ${isDarkMode ? "text-white" : ""}`}>Document Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="upload-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Direction (admin only) */}
          <div>
            <label className={`text-sm font-medium mb-1 block ${isDarkMode ? "text-white" : ""}`}>Direction</label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="upload-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="received">Received (from client)</SelectItem>
                <SelectItem value="sent">Sent (to client)</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <label className={`text-sm font-medium mb-1 block ${isDarkMode ? "text-white" : ""}`}>Notes (optional)</label>
            <Input
              placeholder="e.g. January bank statement"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="upload-notes"
            />
          </div>

          {/* Progress */}
          {uploading && (
            <div className="w-full bg-slate-200 rounded-full h-2" data-testid="upload-progress">
              <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button onClick={handleUpload} disabled={!file || uploading} className="gap-1.5" data-testid="upload-submit">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4" /> Upload</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentsTab;
